<template>
  <div>
    <BaseTitle :title="$t('content.role_title')" />
    <v-row>
      <v-col cols="12" md="auto">
        <v-card class="pa-5">
          <h3 class="role-title">{{ $t('content.role') }}</h3>
          <div v-if="loading" class="text-center mb-3">
            <v-progress-circular indeterminate />
          </div>
          <ul class="role-list">
            <li
              v-for="role in roleWithoutAdmin"
              :key="role.roleId"
              :class="{ active: currentRoleId == role.roleId }"
              @click="getPermissions(role)"
              ref="roleItem"
            >
              <svg-icon icon="customers" class="primary--text mr-3" />
              <span class="role-list-text">{{ roleTranslate(role.roleId) }}</span>
            </li>
          </ul>
        </v-card>
      </v-col>
      <v-col>
        <v-card class="pa-5">
          <h3 class="role-title  mb-4">
            {{ $t('content.permission') }}
            <v-btn
              color="primary"
              class="btn-primary-custom float-right"
              type="submit"
              :loading="table.loading"
              :disabled="!currentRoleId"
              @click="savePermissions"
              disable-pagination
            >
              {{ $t('submit') }}
            </v-btn>
          </h3>
          <v-data-table
            :headers="tableHeader"
            :loading="table.loading"
            :items="tempPermissions"
            hide-default-footer
            disable-pagination
            class="table-custom"
          >
            <template #item.permissionName="{item}">{{ permissionTranslate(item.permissionId) }}</template>
            <template #item.create="{item}">
              <v-simple-checkbox v-model="item.create" color="teal" :disabled="[3, 5].includes(item.roleId) || item.disabled" />
            </template>
            <template #item.read="{item}">
              <v-simple-checkbox v-model="item.view" color="teal" :disabled="item.disabled" />
            </template>
            <template #item.update="{item}">
              <v-simple-checkbox v-model="item.update" color="teal" />
              <!-- <v-simple-checkbox v-model="item.update" color="teal" :disabled="[3, 5].includes(item.roleId) || item.disabled" /> -->
            </template>
            <template #item.delete="{item}">
              <v-simple-checkbox v-model="item.delete" color="teal" :disabled="[3, 5].includes(item.roleId) || item.disabled" />
            </template>
            <template #no-data>
              {{ $t('data_not_available') }}
            </template>
            <template #loading>
              <img src="@/assets/images/globe.gif" class="pa-4" />
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ripple from 'vuetify/lib/directives/ripple';
import BaseTitle from '@/components/base/Title';

export default {
  name: 'RoleIndex',
  components: {
    BaseTitle
  },
  computed: {
    ...mapGetters({
      roles: 'Role/items',
      permissions: 'Role/permissions'
    }),
    roleWithoutAdmin() {
      return this.roles.filter(role => role.roleId !== 1);
    },
    tableHeader() {
      return [
        { text: this.$t('table.menu'), value: 'permissionName', sortable: false },
        { text: this.$t('table.create'), value: 'create', sortable: false, width: 100 },
        { text: this.$t('table.read'), value: 'read', sortable: false, width: 100 },
        { text: this.$t('table.update'), value: 'update', sortable: false, width: 100 },
        { text: this.$t('table.delete'), value: 'delete', sortable: false, width: 100 }
      ];
    }
  },
  directives: {
    ripple
  },
  data: () => ({
    tempPermissions: [],
    currentRoleId: null,
    hasLoad: false,
    loading: true,
    table: {
      loading: false
    }
  }),
  watch: {
    permissions: {
      deep: true,
      immediate: true,
      handler: function(newVal) {
        this.tempPermissions = newVal;
      }
    }
  },
  methods: {
    getData() {
      this.loading = true;
      this.$store
        .dispatch('Role/fetchList')
        .then(() => {
          if (!this.hasLoad) {
            this.$refs.roleItem?.[0].click();
          }
          this.hasLoad = true;
        })
        .finally(() => (this.loading = false));
    },
    getPermissions({ roleId }) {
      this.$store.commit('Role/RESET_PERMISSION');
      this.currentRoleId = roleId;
      this.table.loading = true;
      this.$store
        .dispatch('Role/fetchPermission', roleId)
        .catch(e => this.$store.dispatch('general/showErrorReqNotification', e))
        .finally(() => (this.table.loading = false));
    },
    savePermissions() {
      if (!this.currentRoleId) {
        this.$store.dispatch('general/showErrorNotification', { message: this.$t('error.role_not_selected') });
        return false;
      }

      this.table.loading = true;

      this.$store
        .dispatch('Role/savePermission', { roleId: this.currentRoleId, permissions: this.tempPermissions })
        .then(() => this.$router.push({ name: 'PermissionUpdateSuccess' }))
        .catch(e => this.$store.dispatch('general/showErrorReqNotification', e))
        .finally(() => (this.table.loading = false));
    },
    roleTranslate(roleId) {
      const locales = { 2: 'roles.school_administrator', 3: 'roles.parents', 4: 'roles.teacher', 5: 'roles.student', 6: 'roles.school_advisor' };
      return this.$t(locales[roleId]);
    },
    permissionTranslate(permissionId) {
      const locales = {
        1: 'menu.manage_user',
        2: 'menu.manage_teacher',
        3: 'menu.manage_student',
        4: 'menu.manage_grade',
        5: 'menu.manage_group',
        6: 'menu.course_management',
        7: 'menu.manage_class',
        8: 'menu.attendance',
        9: 'menu.score',
        10: 'menu.assignment',
        11: 'menu.files',
        12: 'menu.schedule',
        13: 'menu.manage_quiz',
        14: 'menu.report',
        15: 'content.syllabus',
        16: 'content.pages',
        17: 'content.modules',
        18: 'content.announcement',
        19: 'content.discussion',
        20: 'content.bank_soal',
        21: 'content.class_section',
        22: 'content.e_rapor'
      };
      return this.$t(locales[permissionId]);
    }
  },
  created() {
    this.$store.commit('table/RESET_PAGEABLE');
    this.$store.commit('Role/RESET_PERMISSION');
    this.getData();
  }
};
</script>

<style lang="scss" scoped>
.role {
  &-title {
    color: #6d6d6d;
    padding-bottom: 15px;
  }

  &-list {
    list-style: none;
    margin: 0;
    padding: 0 !important;
    width: 200px;
    padding-top: 15px !important;
    border-top: 1px dashed #6d6d6d;

    li {
      margin-bottom: 10px;
      text-decoration: none;
      font-weight: 800;
      display: block;
      padding: 10px;
      color: #6d6d6d;
      font-size: 12px;
      border-radius: 4px;
      display: flex;
      align-items: center;
      transition: background 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94), color 200ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
      cursor: pointer;

      &.active {
        background: lighten(#CE8800, 10);
        color: #fff;
      }

      &:hover {
        background: lighten(#CE8800, 10);
        color: #fff;
      }
    }

    &-text {
      flex: 1;
    }
  }
}
</style>
